import { BREAKPOINT_NAV_WRAPPED_MIN } from "@/app/layout/LayoutConstants";
import { Box, Divider, MenuList, Popover, Typography } from "@mui/material";
import {
  bindToggle,
  bindHover,
  bindMenu,
  usePopupState,
} from "material-ui-popup-state/hooks";
import HoverPopover from "material-ui-popup-state/HoverPopover";
import Link from "next/link";

function MenuDivider({ label }: { label: string }) {
  return (
    <Divider
      sx={{
        marginTop: 2,
        "&:first-child": {
          margin: 0,
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>
    </Divider>
  );
}

function MenuItem({
  href = "",
  label,
  isSelected,
  onClick,
  fullRefresh,
  icon,
}: {
  href?: string;
  label: string;
  isSelected?: boolean;
  onClick?: () => void;
  fullRefresh?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <Typography component="li">
      <Box
        component={fullRefresh ? "a" : Link}
        href={href}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            onClick();
          }
        }}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: theme.spacing(1, 2),
          fontWeight: isSelected ? "bold" : "initial",
          fontSize: theme.typography.h6.fontSize,
          "@media (hover: hover)": {
            "&:hover": {
              backgroundColor: "grey.200",
            },
          },
          "img, .MuiSvgIcon-root": {
            width: "24px",
            height: "24px",
          },
        })}
      >
        {icon}
        {label}
      </Box>
    </Typography>
  );
}

export function GlobalNavMenu({
  children,
  menu,
  disableMenu,
  disableMenuOnMobile,
  bindClick,
  menuHeader,
}: {
  children: React.ReactNode;
  menu: Array<{
    type: "divider" | "item";
    label: string;
    href?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    fullRefresh?: boolean;
  }>;
  disableMenu?: boolean;
  disableMenuOnMobile?: boolean;
  bindClick?: boolean;
  menuHeader?: React.ReactNode;
}) {
  const popupState = usePopupState({ variant: "popover" });
  const hasMenu = !disableMenu && menu.length > 0;
  return (
    <Box
      {...(bindClick ? bindToggle(popupState) : bindHover(popupState))}
      sx={{
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: "grey.200",
          },
        },
        cursor: "pointer",
      }}
    >
      {children}

      {hasMenu && (
        <Box
          component={bindClick ? Popover : HoverPopover}
          sx={(theme) => ({
            [theme.breakpoints.down(BREAKPOINT_NAV_WRAPPED_MIN)]: {
              display: disableMenuOnMobile ? "none" : "block",
            },
          })}
          onClick={() => popupState.close()}
          anchorOrigin={{
            horizontal: "center",
            vertical: "bottom",
          }}
          transformOrigin={{
            horizontal: "center",
            vertical: "top",
          }}
          {...bindMenu(popupState)}
        >
          <MenuList>
            {menuHeader}
            {menu.map((item, index) => (
              <>
                {item.type === "divider" && (
                  <MenuDivider key={index} label={item.label} />
                )}
                {item.type === "item" && (
                  <MenuItem
                    key={index}
                    href={item.href}
                    label={item.label}
                    onClick={item.onClick}
                    fullRefresh={item.fullRefresh}
                    icon={item.icon}
                  />
                )}
              </>
            ))}
          </MenuList>
        </Box>
      )}
    </Box>
  );
}
